// A2-638 (operator 2026-11-02, GB return of "Test UK1.xlsx"): every box card
// carried a title, a second description repeating it, two or three chips and
// an (i); boxes at 0 took as much room as real ones; the Northern Ireland
// note was printed under boxes 2, 8 and 9. ONE place decides what a card
// shows, so the card, the folds and the notes cannot disagree:
//   - one title: the form's own words (label_local), in the form's language;
//   - the English behind the (i) only where the form is not in English;
//   - one status per box;
//   - which boxes fold away (0 on the form; not worked out by the app;
//     workings not on the form);
//   - a note shared by several boxes, shown once.
import { figureToType } from "./copyFigure";

// The card's title. A numbered box is "Box <ref>" with the form's words under
// it; a box with no number is titled by the form's words alone.
export const boxTitle = (box) => {
  const ref = box.display_ref === undefined ? box.box_id : box.display_ref;
  return { ref: ref || "", label: box.label_local || box.label_english || "" };
};

// How a box is named in a list ("Not used this period: Box 2, Box 8 …").
export const boxName = (box) => {
  const { ref, label } = boxTitle(box);
  return ref ? `Box ${ref}` : label;   // i18n-ignore
};

// The English behind the (i): only on a form not in English, and only when
// it says something the title does not. Older payloads carry no language
// and keep the English there.
export const englishInInfo = (box, labelLanguage) => {
  const en = box.label_english;
  if (!en || en === box.label_local) return null;
  return labelLanguage === "en" ? null : en;
};

// A2-476 — whether the operator owes this box in the app.
export const boxEntry = (box, { autoFill = false, required = false } = {}) =>
  box.kind !== "typed_in" ? "computed"
    : box.override ? (autoFill ? "app" : "done") : (required ? "required" : "todo");

// The ONE status a card shows (was a role chip plus a kind chip). The key
// names the i18n text; null shows none (a box the app filled says so in its
// saved stamp instead).
export const boxStatus = (box, entry, scale = 1) => {
  const portal = box.portal || "";
  const zero = !box.error && figureToType(box, scale) === 0;
  if (entry === "required") return portal === "enter" ? "portal_enter_first" : "kind_typed_required";
  // the app does not work this box out: a 0 here is not "none this period"
  if (box.kind === "stub_zero") return "kind_stub";
  // review of 895e099a: an optional typed-in box at 0 kept only "0 — nothing
  // to copy", losing "fill it in here if it applies"
  if (portal === "enter" && zero && entry === "todo") return "portal_enter_optional";
  if (portal === "enter") return zero ? "portal_enter_zero" : "portal_enter";
  if (portal === "not_on_form") return box.kind === "typed_in" ? "portal_input" : "portal_not_on_form";
  if (portal === "calculated") return "portal_calculated";
  if (portal === "prefilled") return "portal_prefilled";
  // older payloads, with no role on the form
  if (box.kind === "typed_in") return entry === "todo" ? "kind_optional" : entry === "app" ? null : "kind_typed";
  return box.kind === "computed" ? "kind_computed" : null;
};

// Where a box goes on the page: "shown" in its section, or folded into one
// line: "stub" (the app does not work it out: fill it on the form yourself if
// you have that activity), "unused" (0 as typed on the form), "working" (an
// app-side working not on the form) or "optional" (an input not on the form,
// at 0). A box in error, and one the page must keep in view (the amount to
// pay, a box owed before filing), are always shown; so is any other input. A box jumped to opens its fold instead of
// leaving it (review of 895e099a: moving it scrolled to the wrong place).
export const boxPlace = (box, { scale = 1, keep = false } = {}) => {
  if (keep || box.error) return "shown";
  // A2-638 live check (Test UK1): nine optional UK adjustments (postponed
  // import VAT, reverse-charge services, ...) still took a full card each at
  // 0. An input that is not on the form, at 0 and not owed folds under its
  // own line; the editor is there when the line is opened.
  if (box.kind === "typed_in") {
    return box.portal === "not_on_form" && figureToType(box, scale) === 0 ? "optional" : "shown";
  }
  if (box.kind === "stub_zero") return "stub";
  if (figureToType(box, scale) === 0) return "unused";
  if (box.portal === "not_on_form") return "working";
  return "shown";
};

// A note (out_of_scope_reason) printed under two or more boxes is shown once,
// naming its boxes: {text, boxes}[] in first-seen order.
export const sharedNotes = (boxes) => {
  const by = new Map();
  (boxes || []).forEach((b) => {
    const text = (b.out_of_scope_reason || "").trim();
    if (!text) return;
    if (!by.has(text)) by.set(text, []);
    by.get(text).push(b);
  });
  return [...by.entries()].filter(([, bs]) => bs.length > 1).map(([text, bs]) => ({ text, boxes: bs }));
};
